import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

/**
 * M20 L20.01 — Integration partners registry.
 *
 * The integration "endpoints" store IS the external-partner registry (ERP, CRM,
 * banking, GIS, SSO, email, SMS, WhatsApp, file-exchange). Reads
 * `GET /v1/integration/endpoints` and links each to its detail page.
 */
interface PartnerRow {
  readonly endpointId: string;
  readonly endpointName: string;
  readonly integrationType: string;
  readonly provider: string;
  readonly connectionMode: string;
  readonly status: string;
  readonly lastHealthCheckAt: string | null;
}

const STATUSES = ['', 'ACTIVE', 'INACTIVE', 'DEGRADED', 'SUSPENDED'];

export function IntegrationPartnersPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<PartnerRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');

  useEffect(() => {
    let alive = true;
    setRows(null);
    setError(null);
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    apiRequest<{ data: PartnerRow[] }>(`/v1/integration/endpoints${qs}`, { token })
      .then((r) => { if (alive) setRows(r.data ?? []); })
      .catch((e) => { if (alive) { setError((e as Error).message); setRows([]); } });
    return () => { alive = false; };
  }, [token, status]);

  return (
    <main aria-label="integration-partners">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('m20.partners.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>
        {t('m20.partners.subtitle')}
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        <label htmlFor="status-filter" style={{ fontSize: 12 }}>{t('m20.partners.statusLabel')}</label>
        <select id="status-filter" aria-label="status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
          {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m20.partners.all')}</option>)}
        </select>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        <p>{t('m20.partners.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="partners-empty" style={{ color: 'var(--color-text-muted)' }}>{t('m20.partners.empty')}</p>
      ) : (
        <table aria-label="partners-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--color-border-default)', textAlign: 'left' }}>
              <th style={{ padding: '8px 4px' }}>{t('m20.partners.colName')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m20.partners.colType')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m20.partners.colProvider')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m20.partners.colMode')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m20.partners.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.endpointId} data-testid={`partner-row-${p.endpointId}`} style={{ borderBottom: '1px solid var(--color-border-default)' }}>
                <td style={{ padding: '8px 4px' }}>
                  <Link to={`/admin/integration/partners/${p.endpointId}`}>{p.endpointName}</Link>
                </td>
                <td style={{ padding: '8px 4px' }}>{p.integrationType}</td>
                <td style={{ padding: '8px 4px' }}>{p.provider}</td>
                <td style={{ padding: '8px 4px' }}>{p.connectionMode}</td>
                <td style={{ padding: '8px 4px' }}>{p.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
